// "Download PDF" for a web app (#1578 item 16). A zipped web app cannot run
// from a folder on disk (it fetches its story and data and loads ES modules,
// all blocked on file://), so the download is the browser's own print to
// PDF of the page as it stands in its frame.
//
// The frame is same-origin (served from our API), so its document can be
// given print rules and its window asked to print. The frame's sandbox must
// carry `allow-modals`, or the browser ignores print().

export const WEBAPP_PRINT_STYLE_ID = 'pt-webapp-print'

// A4, the page's own height rather than the frame's, and no chart, table or
// figure split across two sheets.
export const WEBAPP_PRINT_CSS = `
@page { size: A4; margin: 12mm; }
@media print {
  html, body { height: auto !important; overflow: visible !important; }
  pt-chart, pt-table, figure, img, svg, canvas, table { break-inside: avoid; }
  h1, h2, h3 { break-after: avoid; }
  /* A chart's hover tooltip is where the pointer was, not part of the page. */
  .tooltip, [role="tooltip"] { display: none !important; }
}
`

export type PrintOutcome = 'printed' | 'opened'

/**
 * Prints the web app in `frame`. When its document cannot be reached (another
 * origin), opens `fallbackUrl` in a new tab so it can be printed from there.
 */
export function printWebappFrame(frame: HTMLIFrameElement | null, fallbackUrl?: string): PrintOutcome | null {
  let doc: Document | null = null
  try {
    doc = frame?.contentDocument ?? null
  } catch {
    doc = null
  }
  const win = frame?.contentWindow
  if (doc && win) {
    if (!doc.getElementById(WEBAPP_PRINT_STYLE_ID)) {
      const style = doc.createElement('style')
      style.id = WEBAPP_PRINT_STYLE_ID
      style.textContent = WEBAPP_PRINT_CSS
      doc.head?.appendChild(style)
    }
    win.focus()
    win.print()
    return 'printed'
  }
  const url = fallbackUrl || frame?.src
  if (url) {
    window.open(url, '_blank', 'noopener')
    return 'opened'
  }
  return null
}
